import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Contact, CustomFieldDef, NewContact } from '@personical/cc-shared-types';
import { addressLines, mapLink } from '@personical/cc-shared-types';
import type { DuplicateMatch } from '../api.js';
import { useAuth } from '../auth.js';
import { ActivityTimeline } from '../components/ActivityTimeline.js';
import { Avatar } from '../components/Avatar.js';
import { CallTreatmentBadge } from '../components/CallTreatmentBadge.js';
import { ContactForm } from '../components/ContactForm.js';
import { StarButton } from '../components/StarButton.js';
import { useApi } from '../components/api-context.js';
import { downloadFile } from '../csv.js';
import { displayNameFor } from '../display.js';
import { contactToVCard } from '../vcard.js';

export function ContactDetailPage() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { state } = useAuth();
  const currentOid = state.kind === 'authenticated' ? state.user.oid : null;
  const [contact, setContact] = useState<Contact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingNickname, setEditingNickname] = useState(false);
  const [nicknameDraft, setNicknameDraft] = useState('');
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([]);
  const [linkedCompany, setLinkedCompany] = useState<Contact | null>(null);
  const [companyEmployees, setCompanyEmployees] = useState<Contact[] | null>(null);
  const [dupes, setDupes] = useState<DuplicateMatch[]>([]);
  const [merging, setMerging] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setContact(null);
    setError(null);
    api
      .get(id)
      .then((c) => {
        if (!cancelled) setContact(c);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [id, api]);

  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then((s) => {
        if (!cancelled) setCustomFields(s.customFields ?? []);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    setDupes([]);
    if (!contact) return;
    if (contact.phoneNumbers.length === 0 && contact.emails.length === 0) return;
    api
      .checkDuplicates({
        phoneNumbers: contact.phoneNumbers.map((p) => ({ number: p.number })),
        emails: contact.emails.map((e) => ({ address: e.address })),
        excludeId: contact.id
      })
      .then((m) => {
        if (!cancelled) setDupes(m);
      })
      .catch(() => {
        if (!cancelled) setDupes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, contact]);

  useEffect(() => {
    let cancelled = false;
    setLinkedCompany(null);
    setCompanyEmployees(null);
    if (!contact) return;
    if ((contact.kind ?? 'person') === 'person' && contact.companyId) {
      api
        .get(contact.companyId)
        .then((c) => {
          if (!cancelled) setLinkedCompany(c);
        })
        .catch(() => {
          if (!cancelled) setLinkedCompany(null);
        });
    }
    if ((contact.kind ?? 'person') === 'company') {
      api
        .list({ kind: 'person', limit: 100 })
        .then((res) => {
          if (cancelled) return;
          setCompanyEmployees(res.items.filter((p) => p.companyId === contact.id));
        })
        .catch(() => {
          if (!cancelled) setCompanyEmployees([]);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [api, contact]);

  async function save(input: NewContact) {
    if (!id || !contact) return;
    const etag = (contact as Contact & { _etag?: string })._etag;
    const updated = await api.update(id, input, etag ? { etag } : undefined);
    setContact(updated);
    setEditing(false);
  }

  async function remove() {
    if (!id) return;
    if (!confirm('Move this contact to the trash? You can restore it later from the Trash page.')) return;
    setDeleting(true);
    try {
      await api.remove(id);
      navigate('/contacts');
    } catch (err) {
      setError((err as Error).message);
      setDeleting(false);
    }
  }

  async function saveNickname() {
    if (!contact) return;
    try {
      const updated = await api.setNickname(contact.id, nicknameDraft);
      setContact(updated);
      setEditingNickname(false);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function mergeFrom(other: DuplicateMatch) {
    if (!contact) return;
    if (
      !confirm(
        `Merge "${other.displayName}" into "${contact.displayName}"? The other contact's phones, emails, tags, notes, favourites, and nicknames will be combined into this one, and the source contact will be moved to trash.`
      )
    ) {
      return;
    }
    setMerging(other.id);
    try {
      const merged = await api.merge(contact.id, other.id);
      setContact(merged);
      setDupes((prev) => prev.filter((d) => d.id !== other.id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setMerging(null);
    }
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 text-sm rounded p-3">{error}</div>
        <button
          type="button"
          onClick={() => navigate('/contacts')}
          className="text-sm text-mid hover:text-strong"
        >
          ← Back to contacts
        </button>
      </div>
    );
  }

  if (!contact) return <p className="text-sm text-muted">Loading…</p>;

  if (editing) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold">Edit contact</h2>
        <div className="surface rounded-2xl shadow p-6">
          <ContactForm
            submitLabel="Save changes"
            customFields={customFields}
            api={api}
            excludeId={contact.id}
            initial={{
              displayName: contact.displayName,
              jobTitle: contact.jobTitle,
              department: contact.department,
              address: contact.address,
              company: contact.company,
              website: contact.website,
              emails: contact.emails,
              phoneNumbers: contact.phoneNumbers,
              notes: contact.notes,
              tags: contact.tags,
              callTreatment: contact.callTreatment ?? 'normal',
              visibility: contact.visibility ?? 'shared',
              kind: contact.kind ?? 'person',
              companyId: contact.companyId,
              custom: contact.custom ?? {}
            }}
            onSubmit={save}
            onCancel={() => setEditing(false)}
          />
        </div>
      </div>
    );
  }

  const myNickname = currentOid ? contact.nicknames?.[currentOid] : undefined;
  const shownName = displayNameFor(contact, currentOid);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <button
          type="button"
          onClick={() => navigate('/contacts')}
          className="text-sm text-mid hover:text-strong"
        >
          ← Back to contacts
        </button>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() =>
              downloadFile(
                `${contact.displayName.replace(/[^a-z0-9]+/gi, '_')}.vcf`,
                contactToVCard(contact),
                'text/vcard'
              )
            }
            className="btn-secondary px-3 py-1.5 text-sm font-medium"
          >
            vCard
          </button>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="btn-secondary px-3 py-1.5 text-sm font-medium"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={remove}
            disabled={deleting}
            className="rounded-lg border border-red-300 dark:border-red-900 bg-white dark:bg-slate-900 text-red-700 dark:text-red-300 px-3 py-1.5 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>

      {dupes.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-900 text-amber-900 dark:text-amber-200 text-sm rounded-2xl p-4 space-y-2">
          <p className="font-medium">
            Possible duplicate{dupes.length === 1 ? '' : 's'} of this contact
          </p>
          <ul className="space-y-1.5">
            {dupes.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/contacts/${d.id}`}
                  className="underline font-medium"
                >
                  {d.displayName}
                </Link>
                <span className="text-xs">
                  matches by {d.reason} <span className="font-mono">{d.matchedValue}</span>
                </span>
                <span className="flex-1" />
                <button
                  type="button"
                  onClick={() => mergeFrom(d)}
                  disabled={merging === d.id}
                  className="rounded-md border border-amber-300 dark:border-amber-700 bg-white dark:bg-slate-900 text-amber-900 dark:text-amber-200 px-2.5 py-1 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50"
                  title={`Merge "${d.displayName}" into this contact`}
                >
                  {merging === d.id ? 'Merging…' : 'Merge in'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <article className="surface rounded-2xl shadow p-6 space-y-5">
        <header className="flex items-center gap-4">
          <PhotoEditor
            name={shownName}
            photoUrl={contact.photoUrl}
            onUpload={async (file) => {
              try {
                const updated = await api.uploadPhoto(contact.id, file);
                setContact(updated);
              } catch (err) {
                setError((err as Error).message);
              }
            }}
            onRemove={async () => {
              if (!confirm('Remove the photo?')) return;
              try {
                const updated = await api.deletePhoto(contact.id);
                setContact(updated);
              } catch (err) {
                setError((err as Error).message);
              }
            }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-2xl font-semibold">{shownName}</h2>
              {(contact.kind ?? 'person') === 'company' && (
                <span className="inline-flex items-center rounded-full bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-300 ring-1 ring-sky-200 dark:ring-sky-900 text-xs uppercase tracking-wider px-2 py-0.5">
                  Company
                </span>
              )}
              <CallTreatmentBadge treatment={contact.callTreatment} size="md" />
              {contact.visibility === 'private' && (
                <span className="inline-flex items-center rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-300 ring-1 ring-violet-200 dark:ring-violet-900 text-xs uppercase tracking-wider px-2 py-0.5">
                  Private
                </span>
              )}
              {currentOid && (
                <StarButton
                  filled={(contact.favoritedBy ?? []).includes(currentOid)}
                  size={22}
                  onClick={async (e) => {
                    e.preventDefault();
                    const isFav = (contact.favoritedBy ?? []).includes(currentOid);
                    try {
                      const updated = await api.setFavorite(contact.id, !isFav);
                      setContact(updated);
                    } catch (err) {
                      setError((err as Error).message);
                    }
                  }}
                />
              )}
            </div>
            {myNickname && (
              <p className="text-xs text-muted">
                Shared name: <span className="text-mid">{contact.displayName}</span>
              </p>
            )}
            <p className="text-sm text-muted">
              {[contact.jobTitle, contact.department, contact.company].filter(Boolean).join(' · ') || '—'}
            </p>
          </div>
        </header>

        {currentOid && (
          <div className="text-sm">
            {editingNickname ? (
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={nicknameDraft}
                  onChange={(e) => setNicknameDraft(e.target.value)}
                  placeholder={contact.displayName}
                  className="flex-1 rounded-md border border-input bg-white px-3 py-1.5 text-sm"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={saveNickname}
                  className="btn-primary px-3 py-1.5 text-sm font-medium"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setEditingNickname(false)}
                  className="rounded-md border border-input bg-white px-3 py-1.5 text-sm font-medium hover:surface-muted"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setNicknameDraft(myNickname ?? '');
                  setEditingNickname(true);
                }}
                className="text-xs text-muted hover:text-strong underline"
              >
                {myNickname ? 'Change your nickname' : 'Set a nickname (just for you)'}
              </button>
            )}
          </div>
        )}

        {linkedCompany && (
          <Section title="Works at">
            <Link
              to={`/contacts/${linkedCompany.id}`}
              className="inline-flex items-center gap-2 rounded-lg surface-muted hover:bg-slate-200 dark:hover:bg-slate-700 px-3 py-2"
            >
              <Avatar name={linkedCompany.displayName} photoUrl={linkedCompany.photoUrl} size="sm" />
              <span className="font-medium">{linkedCompany.displayName}</span>
            </Link>
          </Section>
        )}

        {(contact.kind ?? 'person') === 'company' &&
          companyEmployees &&
          companyEmployees.length > 0 && (
            <Section title={`People at ${contact.displayName}`}>
              <ul className="flex flex-wrap gap-2">
                {companyEmployees.map((p) => (
                  <li key={p.id}>
                    <Link
                      to={`/contacts/${p.id}`}
                      className="inline-flex items-center gap-2 rounded-lg surface-muted hover:bg-slate-200 dark:hover:bg-slate-700 px-3 py-1.5 text-sm"
                    >
                      <Avatar name={p.displayName} photoUrl={p.photoUrl} size="sm" />
                      <span>{p.displayName}</span>
                      {p.jobTitle && (
                        <span className="text-xs text-muted">· {p.jobTitle}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </Section>
          )}

        {contact.emails.length > 0 && (
          <Section title="Emails">
            <ul className="text-sm space-y-1">
              {contact.emails.map((e, i) => (
                <li key={i}>
                  <span className="text-xs uppercase tracking-wider text-faint mr-2">{e.type}</span>
                  <a href={`mailto:${e.address}`} className="text-strong underline">
                    {e.address}
                  </a>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {contact.phoneNumbers.length > 0 && (
          <Section title="Phone numbers">
            <ul className="text-sm space-y-1.5">
              {contact.phoneNumbers.map((p, i) => {
                const dialTarget = p.e164 ?? p.number;
                return (
                  <li key={i} className="flex flex-wrap items-center gap-2">
                    <span className="text-xs uppercase tracking-wider text-faint w-12">
                      {p.type}
                    </span>
                    <span className="text-strong">{p.number}</span>
                    {p.e164 && p.e164 !== p.number.replace(/\s+/g, '') && (
                      <span className="text-xs text-faint">{p.e164}</span>
                    )}
                    <a
                      href={`tel:${dialTarget}`}
                      className="rounded-md border border-input bg-white px-2 py-0.5 text-xs hover:surface-muted"
                      title="Call"
                    >
                      Call
                    </a>
                    <a
                      href={`sms:${dialTarget}`}
                      className="rounded-md border border-input bg-white px-2 py-0.5 text-xs hover:surface-muted"
                      title="Send SMS"
                    >
                      SMS
                    </a>
                  </li>
                );
              })}
            </ul>
          </Section>
        )}

        <AddressSection contact={contact} />

        {contact.website && (
          <Section title="Website">
            <a
              href={absoluteUrl(contact.website)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-strong underline break-all"
            >
              {contact.website}
            </a>
          </Section>
        )}

        {contact.tags.length > 0 && (
          <Section title="Tags">
            <ul className="flex flex-wrap gap-1.5">
              {contact.tags.map((t) => (
                <li key={t}>
                  <Link
                    to={`/contacts?tag=${encodeURIComponent(t)}`}
                    className="block rounded-full surface-muted hover:bg-slate-200 dark:hover:bg-slate-700 text-mid text-xs px-2.5 py-0.5"
                  >
                    {t}
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {contact.notes && (
          <Section title="Notes">
            <p className="text-sm whitespace-pre-wrap text-mid">{contact.notes}</p>
          </Section>
        )}

        {customFields.length > 0 &&
          customFields.some((f) => contact.custom?.[f.id]) && (
            <Section title="Additional fields">
              <dl className="text-sm grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
                {customFields.map((f) => {
                  const v = contact.custom?.[f.id];
                  if (!v) return null;
                  return (
                    <div key={f.id} className="contents">
                      <dt className="text-xs uppercase tracking-wider text-faint">{f.label}</dt>
                      <dd className="text-mid break-all">
                        {f.type === 'url' ? (
                          <a
                            href={absoluteUrl(v)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            {v}
                          </a>
                        ) : (
                          v
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </Section>
          )}

        <ActivityTimeline
          api={api}
          contactId={contact.id}
          defaultPhoneNumber={contact.phoneNumbers[0]?.e164 ?? contact.phoneNumbers[0]?.number}
          currentOid={currentOid}
        />

        <footer className="text-xs text-faint pt-4 border-t border-divider space-y-0.5">
          <p>
            Created {new Date(contact.createdAt).toLocaleString()}
            {currentOid && contact.createdBy === currentOid ? ' by you' : ''}
          </p>
          <p>
            Updated {new Date(contact.updatedAt).toLocaleString()}
            {currentOid && contact.updatedBy === currentOid ? ' by you' : ''}
          </p>
        </footer>
      </article>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-xs uppercase tracking-wider text-muted mb-1">{title}</h3>
      {children}
    </section>
  );
}

function AddressSection({ contact }: { contact: Contact }) {
  const lines = addressLines(contact.address, navigator.language);
  if (lines.length === 0) return null;
  const map = mapLink(contact.address);
  return (
    <Section title="Address">
      <address className="text-sm not-italic text-strong">
        {lines.map((l, i) => (
          <span key={i} className="block">
            {l}
          </span>
        ))}
      </address>
      {map && (
        <a
          href={map}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-1 text-xs text-muted hover:text-strong underline"
        >
          Show on map
        </a>
      )}
    </Section>
  );
}

function absoluteUrl(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function PhotoEditor({
  name,
  photoUrl,
  onUpload,
  onRemove
}: {
  name: string;
  photoUrl?: string;
  onUpload(file: File): Promise<void> | void;
  onRemove(): Promise<void> | void;
}) {
  return (
    <div className="relative group">
      <Avatar name={name} photoUrl={photoUrl} size="lg" />
      <label
        className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 text-white text-[10px] uppercase tracking-wider opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition cursor-pointer"
        title="Change photo"
      >
        Edit
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onUpload(file);
            e.target.value = '';
          }}
        />
      </label>
      {photoUrl && (
        <button
          type="button"
          onClick={() => void onRemove()}
          aria-label="Remove photo"
          className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs leading-none flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
        >
          ×
        </button>
      )}
    </div>
  );
}
